import { useTravelataApiService } from '@/shared/composables/useTravelataApiService';
import { useTravelataAppConfig } from '@/shared/composables/useTravelataAppConfig';
import { serializeSearchParams } from './serializeSearchParams';
import type {
  ApiProductSearchCacheResponse,
  ApiProductSearchSessionResponse,
  ApiProductSearchSessionStatusResponse,
  ProductSearchCacheParams,
  ProductSearchCacheResult,
  ProductSearchSession,
  ProductSearchSessionParams,
  ProductSearchSessionStatus,
  ProductSearchSessionStatusParams,
} from './types';

const SEARCH_CACHE_REQUEST_TIMEOUT_MS = 0;
const SEARCH_SESSION_REQUEST_TIMEOUT_MS = 40000;

const FORM_URLENCODED_HEADERS = { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' };

export class ProductSearchResponseError extends Error {
  override readonly name = 'ProductSearchResponseError';

  constructor(readonly path: string, message?: string | null) {
    super(message || `productSearch: ${path} ответил без success`);
  }
}

function buildUrl(path: string): string {
  return `${useTravelataAppConfig().apiGatewayUrl}${path}`;
}

function toCount(value: number | string | undefined): number {
  const count = Number(value);

  return Number.isFinite(count) ? count : 0;
}

export async function fetchProductSearchCache(
  path: string,
  params: ProductSearchCacheParams,
  signal?: AbortSignal,
): Promise<ProductSearchCacheResult> {
  const apiService = useTravelataApiService();
  const response = await apiService.get<ApiProductSearchCacheResponse>(
    `${buildUrl(path)}?${serializeSearchParams(params)}`,
    { signal, timeout: SEARCH_CACHE_REQUEST_TIMEOUT_MS },
  );
  if (response?.success !== true || !response.result) {
    throw new ProductSearchResponseError(path, response?.message);
  }

  return {
    ...response.result,
    hotels: response.result.hotels ?? [],
    countries: response.result.countries ?? [],
    operators: response.result.operators ?? [],
    firstPaymentDefinitions: response.result.firstPaymentDefinitions ?? [],
  };
}

export async function createProductSearchSession(
  path: string,
  params: ProductSearchSessionParams,
  signal?: AbortSignal,
): Promise<ProductSearchSession> {
  const apiService = useTravelataApiService();
  const response = await apiService.post<ApiProductSearchSessionResponse, string>(
    buildUrl(path),
    serializeSearchParams(params),
    { headers: FORM_URLENCODED_HEADERS, signal, timeout: SEARCH_SESSION_REQUEST_TIMEOUT_MS },
  );

  const id = response?.result?.id;
  if (response?.success !== true || id === undefined || id === null || id === '') {
    throw new ProductSearchResponseError(path, response?.message);
  }

  return { id: String(id) };
}

export async function getProductSearchSessionStatus(
  path: string,
  { sessionId, ...params }: ProductSearchSessionStatusParams,
  signal?: AbortSignal,
): Promise<ProductSearchSessionStatus> {
  const apiService = useTravelataApiService();
  // Статус опрашивается по одному и тому же URL — метка `_` (как jQuery cache:false)
  // не даёт браузеру отдать закешированный ответ.
  const query = serializeSearchParams({ ...params, _: Date.now() });
  const response = await apiService.get<ApiProductSearchSessionStatusResponse>(
    `${buildUrl(path)}/${encodeURIComponent(sessionId)}?${query}`,
    { signal, timeout: SEARCH_SESSION_REQUEST_TIMEOUT_MS },
  );
  const data = response?.result ?? response?.data;

  return {
    success: response?.success === true,
    message: response?.message ?? null,
    jobsCount: toCount(data?.jobsCount),
    jobsSuccess: toCount(data?.jobsSuccess),
    operators: data?.tracking?.operators ?? {},
  };
}
